// src/pages/NewForumTopic.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import MentionTextarea from '@/components/mentions/MentionTextarea';

import { ArrowLeft, Loader2, MessageSquare, X, ImagePlus } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

import { getForumCategories, createTopic } from '@/lib/supabase/forum';
import { ForumCategory } from '@/types/forum';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import { supabase } from '@/lib/supabase';
import type { Member } from '@/types/user';
import { addNotification } from '@/lib/supabase/notifications';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';

const NewForumTopic: React.FC = () => {
  const navigate       = useNavigate();
  const location       = useLocation();
  const { clubId }     = useParams<{ clubId?: string }>();
  const insideClub     = Boolean(clubId);
  const { isDarkMode } = useTheme();

  const initialCategoryId = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    return qp.get('category') || '';
  }, [location.search]);

  /* ─────────── state ─────────── */
  const [categories,   setCategories]   = useState<ForumCategory[]>([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  const [title,      setTitle]      = useState('');
  const [content,    setContent]    = useState('');
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // track mentions in topic
  const [mentionedUsers, setMentionedUsers] = useState<Member[]>([]);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.webm', '.mov']
    },
    maxSize: 50 * 1024 * 1024, // 50MB max
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedMedia(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setMediaPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0]?.message || 'Invalid file';
      setError(error);
    }
  });

  /* ─────────── fetch categories ─────────── */
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        let cats: ForumCategory[] = [];

        if (clubId) {
          const [categoryRes, adminRes, sessionRes] = await Promise.all([
            supabase.rpc('get_club_forum_categories', { club_id_param: clubId }),
            supabase.rpc('check_user_is_club_admin',    { club_id_param: clubId }),
            supabase.auth.getSession()
          ]);

          const allCats    = categoryRes.data || [];
          const isAdmin    = adminRes.data === true;
          const isLoggedIn = !!sessionRes.data.session?.user;

          const visibleCats = allCats.filter((c: any) => {
            if (isAdmin)      return true;
            if (!isLoggedIn)  return c.visibility === 'public';
            return c.visibility === 'public' || c.visibility === 'members';
          });

          cats = visibleCats.map((c: any) => ({
            id:           c.id,
            name:         c.name,
            slug:         c.slug,
            description:  '',
            icon:         '',
            order:        c.ordering ?? 0,
            topicsCount:  0,
            postsCount:   0,
            lastActivity: null
          }));
        } else {
          cats = await getForumCategories();
        }

        setCategories(cats);

        if (initialCategoryId && cats.some(c => c.id === initialCategoryId)) {
          setCategoryId(initialCategoryId);
        } else if (cats.length) {
          setCategoryId(cats[0].id);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [clubId, initialCategoryId]);

  /* ─────────── create topic ─────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !categoryId) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    // Progress bar animation for media uploads
    let progressInterval: NodeJS.Timeout | null = null;
    if (selectedMedia) {
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 5, 95));
      }, 100);
    }

    try {
      const newTopic = await createTopic(title, content, categoryId, clubId, selectedMedia);
      if (!newTopic) {
        setError('Failed to create topic');
      } else {
        // notify mentioned users
        console.log('[Topic Mention] will notify:', mentionedUsers.map(u => u.id));
        const {
  data: { user }
} = await supabase.auth.getUser();
if (!user?.id) {
  throw new Error('Not authenticated');
}

const { data: senderProfile, error: senderProfileError } = await supabase
  .from('users')
  .select('id, full_name')
  .eq('auth_id', user.id) 
  .single();

        if (senderProfileError || !senderProfile?.id) {
          console.error('Could not fetch sender profile id', senderProfileError);
        } else {
          const senderProfileId = senderProfile.id;
          const senderName      = senderProfile.full_name;
          for (const u of mentionedUsers) {
            try {
              await addNotification({
                type: 'mention',
                content: `${senderName} mentioned you in a forum topic`,
                user_id: u.id,
                sender_id: senderProfileId,
                metadata: { topicId: newTopic.id },
              });
            } catch (err) {
              console.error('[Topic Mention] addNotification error for', u.id, err);
            }
          }
        }
        setMentionedUsers([]);

        const base = insideClub
          ? `/dashboard/clubs/${clubId}/forum/topic/${newTopic.id}`
          : `/dashboard/forums/topic/${newTopic.id}`;
        navigate(base);
      }
    } catch (err) {
      console.error('Error creating topic:', err);
      setError('Failed to create topic');
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
        setUploadProgress(0);
      }
      setIsSubmitting(false);
    }
  };

  /* ─────────── page body ─────────── */
  const PageBody = (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-[120px] space-y-6">
      {/* Back */}
      <button
        onClick={() => insideClub
          ? navigate(`/dashboard/clubs/${clubId}`)
          : navigate('/dashboard/forums')}
        className="flex items-center text-dark-800 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back
      </button>

      {/* Heading */}
      <div className={cn('pb-4 border-b', isDarkMode ? 'border-dark-300' : 'border-light-300')}>
        <h1 className="text-2xl font-bold text-white">Create New Topic</h1>
        <p className="text-dark-800 mt-1">
          {insideClub
            ? "Start a discussion in this club's forum"
            : 'Share your thoughts in the global forum'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6" {...getRootProps()}>
        <input {...getInputProps()} />
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">{error}</div>
        )}

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="category" className="text-dark-800">Category</Label>
          <Select
            value={categoryId}
            onValueChange={setCategoryId}
            disabled={isLoading || isSubmitting}
          >
            <SelectTrigger className={cn(
              'w-full',
              isDarkMode ? 'bg-dark-300 border-dark-400 text-white' : 'bg-light-200 border-light-300 text-light-900'
            )}>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent className={cn(
              isDarkMode ? 'bg-dark-200 border-dark-300' : 'bg-light-100 border-light-300'
            )}>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title" className="text-dark-800">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Enter a descriptive title"
            className={cn(
              isDarkMode ? 'bg-dark-300 border-dark-400 text-white' : 'bg-light-200 border-light-300 text-light-900'
            )}
            maxLength={100}
            disabled={isSubmitting}
          />
        </div>

        {/* Content */}
        <div className="space-y-2">
          <Label htmlFor="content" className="text-dark-800">Content</Label>
          <MentionTextarea
            value={content}
            onChange={(newContent, mentions) => {
              setContent(newContent);
              setMentionedUsers(mentions);
            }}
            placeholder="Write your topic content here..."
            className={cn(
              'w-full min-h-[200px]',
              isDarkMode ? 'bg-dark-300 border-dark-400 text-white' : 'bg-light-200 border-light-300 text-light-900'
            )}
            disabled={isSubmitting}
          />
        </div>

        {/* Media upload button */}
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            className={cn(
              "text-dark-800 hover:text-white border-dark-400 hover:bg-dark-400",
              "transition-colors"
            )}
            disabled={isSubmitting}
          >
            <ImagePlus className="h-5 w-5 mr-2" />
            <span>Add Image or Video</span>
          </Button>
        </div>
        
        {/* Media preview */}
        <AnimatePresence>
          {mediaPreview && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="relative rounded-lg overflow-hidden border border-dark-400"
            >
              {selectedMedia?.type.startsWith('video/') ? (
                <video 
                  src={mediaPreview} 
                  className="max-h-64 w-auto mx-auto" 
                  controls
                />
              ) : (
                <img 
                  src={mediaPreview} 
                  alt="Preview" 
                  className="max-h-64 w-auto mx-auto" 
                />
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMedia(null);
                  setMediaPreview(null);
                }}
                className="absolute top-2 right-2 p-1 rounded-full bg-dark-900/80 text-white hover:bg-dark-900"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Upload progress indicator */}
        {isSubmitting && selectedMedia && uploadProgress > 0 && (
          <div className="mt-2">
            <div className="h-1 w-full bg-dark-300 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gold-400 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-dark-800 mt-1">Uploading media... {uploadProgress}%</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => insideClub
              ? navigate(`/dashboard/clubs/${clubId}`)
              : navigate('/dashboard/forums')}
            disabled={isSubmitting}
            className={cn(
              isDarkMode ? 'border-dark-400 text-white hover:bg-dark-400' : 'border-light-300 text-light-900 hover:bg-light-200'
            )}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !title.trim() || !content.trim() || !categoryId}
            className="bg-gold-400 hover:bg-gold-500 text-dark-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <MessageSquare className="h-4 w-4 mr-2" />
                Create Topic
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );

  return insideClub
    ? PageBody
    : <DashboardLayout>{PageBody}</DashboardLayout>;
};

export default NewForumTopic;
